/**
 * Reporte de recordatorios multi-canal + respuestas del paciente.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@/presentation/components/common';
import { useReminderAnalytics } from '@/presentation/hooks/useReminderAnalytics';
import type {
  ReminderAnalyticsGrain,
  ReminderAnalyticsQuery,
} from '@/domain/repositories/IReportRepository';

type Preset = 'today' | '7d' | '30d' | '90d' | 'this_week' | 'this_month' | 'last_month' | 'custom';

function KpiCard({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'default' | 'teal' | 'amber' | 'rose' | 'slate';
}) {
  const toneClass =
    tone === 'teal'
      ? 'text-teal-700 dark:text-teal-300'
      : tone === 'amber'
        ? 'text-amber-700 dark:text-amber-300'
        : tone === 'rose'
          ? 'text-rose-700 dark:text-rose-300'
          : tone === 'slate'
            ? 'text-slate-600 dark:text-slate-300'
            : 'text-slate-900 dark:text-white';

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3 dark:border-gray-700 dark:bg-gray-800">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums leading-none ${toneClass}`}>
        {typeof value === 'number' ? value.toLocaleString('es-AR') : value}
      </p>
      {hint ? (
        <p className="mt-1.5 text-[11px] leading-snug text-slate-500 dark:text-slate-400">{hint}</p>
      ) : null}
    </div>
  );
}

export const ReminderAnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<Preset>('30d');
  const [grain, setGrain] = useState<ReminderAnalyticsGrain>('day');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [search, setSearch] = useState('');
  const [includeOutcomes, setIncludeOutcomes] = useState(true);

  const query: ReminderAnalyticsQuery = useMemo(() => {
    const base: ReminderAnalyticsQuery = {
      grain,
      includeOutcomes,
      tenantId: tenantId.trim() || undefined,
    };
    if (preset === 'custom') {
      return {
        ...base,
        from: from || undefined,
        to: to || undefined,
      };
    }
    return { ...base, preset };
  }, [preset, grain, from, to, tenantId, includeOutcomes]);

  const { report, isLoading, error, refetch } = useReminderAnalytics(query);

  const filteredTenants = useMemo(() => {
    const rows = report?.tenants ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        r.nombre.toLowerCase().includes(term) ||
        r.estado.toLowerCase().includes(term) ||
        r.tenantId.toLowerCase().includes(term)
    );
  }, [report, search]);

  const o = report?.outcomes;
  const totals = report?.totals;

  return (
    <div className="panel-page h-full min-w-0 flex flex-col !pb-3 overflow-hidden">
      <div className="panel-page-header flex-shrink-0 !items-end">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.reminderAnalytics.title')}</h1>
          <p className="panel-page-subtitle hidden sm:block">
            {t('pages.reminderAnalytics.subtitle')}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void refetch()}>
          {t('common.refresh')}
        </Button>
      </div>

      {/* Filtros */}
      <div className="mb-3 flex-shrink-0 space-y-2 rounded-xl border border-slate-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-wrap gap-1.5">
          {(
            [
              ['today', t('pages.reminderAnalytics.presetToday')],
              ['7d', t('pages.reminderAnalytics.preset7d')],
              ['30d', t('pages.reminderAnalytics.preset30d')],
              ['90d', t('pages.reminderAnalytics.preset90d')],
              ['this_week', t('pages.reminderAnalytics.presetThisWeek')],
              ['this_month', t('pages.reminderAnalytics.presetThisMonth')],
              ['last_month', t('pages.reminderAnalytics.presetLastMonth')],
              ['custom', t('pages.reminderAnalytics.presetCustom')],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setPreset(id)}
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                preset === id
                  ? 'bg-teal-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-gray-700 dark:text-slate-200 dark:hover:bg-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="mb-1 text-[11px] font-medium uppercase text-slate-500">
              {t('pages.reminderAnalytics.grain')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(
                [
                  ['day', t('pages.reminderAnalytics.grainDay')],
                  ['week', t('pages.reminderAnalytics.grainWeek')],
                  ['month', t('pages.reminderAnalytics.grainMonth')],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setGrain(id)}
                  className={`rounded-md px-2.5 py-1.5 text-xs font-medium ${
                    grain === id
                      ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                      : 'border border-slate-200 text-slate-700 dark:border-gray-600 dark:text-slate-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {preset === 'custom' ? (
            <>
              <Input
                label={t('pages.reminderAnalytics.from')}
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
              <Input
                label={t('pages.reminderAnalytics.to')}
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </>
          ) : (
            <div className="sm:col-span-2">
              <p className="mb-1 text-[11px] font-medium uppercase text-slate-500">
                {t('pages.reminderAnalytics.range')}
              </p>
              <p className="text-sm font-semibold tabular-nums text-slate-800 dark:text-slate-100">
                {report?.filters.from ?? '—'} → {report?.filters.to ?? '—'}
              </p>
            </div>
          )}

          <Input
            label={t('pages.reminderAnalytics.tenantId')}
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder={t('pages.reminderAnalytics.tenantIdPlaceholder')}
          />
        </div>

        <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={includeOutcomes}
            onChange={(e) => setIncludeOutcomes(e.target.checked)}
            className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
          />
          {t('pages.reminderAnalytics.includeOutcomes')}
        </label>
      </div>

      {error ? (
        <div className="mb-3 flex-shrink-0 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          {error.message}
        </div>
      ) : null}

      {isLoading && !report ? (
        <div className="flex h-40 items-center justify-center text-gray-500">
          {t('common.loading')}
        </div>
      ) : report && totals && o ? (
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
          {/* KPI canales */}
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <KpiCard
              label={t('pages.reminderAnalytics.kpiTotal')}
              value={totals.total}
              hint={t('pages.reminderAnalytics.kpiTotalHint', {
                failed: totals.failed,
                pending: totals.pending,
              })}
              tone="teal"
            />
            <KpiCard
              label={t('pages.reminderAnalytics.kpiWhatsapp')}
              value={totals.whatsapp}
              tone="default"
            />
            <KpiCard
              label={t('pages.reminderAnalytics.kpiEmail')}
              value={totals.email}
              tone="default"
            />
            <KpiCard
              label={t('pages.reminderAnalytics.kpiPush')}
              value={totals.push}
              tone="default"
            />
          </div>

          {/* KPI outcomes */}
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <KpiCard
              label={t('pages.reminderAnalytics.kpiAccepted')}
              value={o.confirmedTotal}
              hint={t('pages.reminderAnalytics.kpiAcceptedHint', {
                wa: o.confirmedWhatsapp,
                app: o.confirmedApp,
              })}
              tone="teal"
            />
            <KpiCard
              label={t('pages.reminderAnalytics.kpiConfirmedByChannel')}
              value={o.confirmedWhatsapp}
              hint={t('pages.reminderAnalytics.kpiConfirmedByChannelHint')}
            />
            <KpiCard
              label={t('pages.reminderAnalytics.kpiRejected')}
              value={o.rejectedWhatsapp + o.rejectedApp}
              hint={t('pages.reminderAnalytics.kpiRejectedHint', {
                wa: o.rejectedWhatsapp,
                app: o.rejectedApp,
              })}
              tone="rose"
            />
            <KpiCard
              label={t('pages.reminderAnalytics.kpiRescheduled')}
              value={o.rescheduled}
              hint={t('pages.reminderAnalytics.kpiRescheduledHint', {
                app: o.rescheduledByOrigin.app,
                wa: o.rescheduledByOrigin.whatsapp,
              })}
              tone="amber"
            />
          </div>

          {/* Serie temporal */}
          <div className="rounded-xl border border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <div className="border-b border-slate-100 px-3 py-2.5 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                {t('pages.reminderAnalytics.seriesTitle')}
              </h2>
            </div>
            <div className="max-h-64 overflow-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-gray-700">
                <thead className="sticky top-0 bg-slate-50 text-left text-[11px] uppercase text-slate-500 dark:bg-gray-900 dark:text-slate-400">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('pages.reminderAnalytics.colPeriod')}</th>
                    <th className="px-3 py-2 text-right font-medium">WA</th>
                    <th className="px-3 py-2 text-right font-medium">Email</th>
                    <th className="px-3 py-2 text-right font-medium">App</th>
                    <th className="px-3 py-2 text-right font-medium">{t('pages.reminderAnalytics.colTotal')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('pages.reminderAnalytics.colOk')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('pages.reminderAnalytics.colNo')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('pages.reminderAnalytics.colResch')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                  {report.series.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-3 py-6 text-center text-slate-500">
                        {t('pages.reminderAnalytics.empty')}
                      </td>
                    </tr>
                  ) : (
                    report.series.map((row) => (
                      <tr key={row.period} className="hover:bg-slate-50/80 dark:hover:bg-gray-700/40">
                        <td className="px-3 py-2 font-medium tabular-nums text-slate-800 dark:text-slate-100">
                          {row.period}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.whatsapp.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.email.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.push.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums">{row.total.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-teal-700 dark:text-teal-300">
                          {row.confirmedWhatsapp.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-rose-600 dark:text-rose-300">
                          {(row.rejectedWhatsapp + row.rejectedApp).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-700 dark:text-amber-300">
                          {row.rescheduled.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Por clínica */}
          <div className="rounded-xl border border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-3 py-2.5 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                {t('pages.reminderAnalytics.byClinicTitle')}
              </h2>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('pages.reminderAnalytics.searchClinic')}
                className="max-w-xs"
              />
            </div>
            <div className="max-h-80 overflow-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-gray-700">
                <thead className="sticky top-0 bg-slate-50 text-left text-[11px] uppercase text-slate-500 dark:bg-gray-900 dark:text-slate-400">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('pages.reminderAnalytics.colClinic')}</th>
                    <th className="px-3 py-2 text-right font-medium">WA</th>
                    <th className="px-3 py-2 text-right font-medium">Email</th>
                    <th className="px-3 py-2 text-right font-medium">App</th>
                    <th className="px-3 py-2 text-right font-medium">{t('pages.reminderAnalytics.colTotal')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('pages.reminderAnalytics.colOk')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('pages.reminderAnalytics.colNo')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('pages.reminderAnalytics.colResch')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                  {filteredTenants.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-3 py-6 text-center text-slate-500">
                        {t('pages.reminderAnalytics.empty')}
                      </td>
                    </tr>
                  ) : (
                    filteredTenants.map((row) => (
                      <tr key={row.tenantId} className="hover:bg-slate-50/80 dark:hover:bg-gray-700/40">
                        <td className="px-3 py-2">
                          <p className="font-medium text-slate-900 dark:text-white">{row.nombre}</p>
                          <p className="text-[11px] text-slate-500">{row.estado}</p>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.whatsapp.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.email.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.push.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums">{row.total.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-teal-700 dark:text-teal-300">
                          {row.confirmedWhatsapp.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-rose-600">
                          {(row.rejectedWhatsapp + row.rejectedApp).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-700">
                          {row.rescheduled.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notas */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-slate-400">
            <p className="mb-1 font-medium text-slate-700 dark:text-slate-300">
              {t('pages.reminderAnalytics.notesTitle')}
              {includeOutcomes
                ? ` · ${t('pages.reminderAnalytics.scanStats', {
                    ok: report.tenantOutcomesScanned,
                    fail: report.tenantOutcomesFailed,
                  })}`
                : null}
            </p>
            <ul className="list-inside list-disc space-y-0.5">
              {report.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
};
